import { FC, useMemo, useState, useRef, useEffect, KeyboardEvent } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import styles from './SearchBar.module.scss'
import SearchItem from './search-item/SearchItem'
import { Cross, Search } from 'components/assets/svg'
import { Article } from 'features/encyclopedia/types'
import { routes } from 'utils/routes'
import { SearchBarProps as Props } from './types'

const SearchBar: FC<Props> = ({ className, items, placeholder }) => {
  // ROUTER
  const router = useRouter()

  // Local state
  const [value, setValue] = useState<string>('')
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [selected, setSelected] = useState<{ item: Article; idx: number }>({ item: null, idx: -1 })

  // Refs
  const searchInput = useRef<HTMLInputElement>(null)
  const searchList = useRef<HTMLUListElement>(null)

  // Memoized constants
  const searchResults = useMemo(
    () => items?.filter((article) => article?.title?.toLowerCase()?.includes(value?.toLowerCase()))?.slice(0, 5),
    [items, value]
  )

  /**
   * Hides dropdown on click outside
   */
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        searchList.current &&
        !searchList.current.contains(event.target as HTMLUListElement) &&
        searchInput.current &&
        !searchInput.current.contains(event.target as HTMLInputElement)
      ) {
        setIsFocused(false)
        clearSelected()
      }
    }

    document.addEventListener('click', handleClickOutside, true)

    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  /**
   * Handles the keyboard control of the search component
   *
   * @param {KeyboardEvent<HTMLInputElement>} e
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!!value && isFocused && !!searchResults?.length) {
      if (e.key === 'ArrowDown' || (!e.getModifierState('Shift') && e.key === 'Tab')) {
        e.preventDefault()
        const newIndex = selected.idx + 1 > searchResults.length - 1 ? 0 : selected.idx + 1
        setSelected({ idx: newIndex, item: searchResults[newIndex] })
      }

      if (e.key === 'ArrowUp' || (e.getModifierState('Shift') && e.key === 'Tab')) {
        e.preventDefault()
        const newIndex = selected.idx - 1 < 0 ? searchResults.length - 1 : selected.idx - 1
        setSelected({ idx: newIndex, item: searchResults[newIndex] })
      }

      if (e.key === 'Escape') {
        setValue('')
        clearSelected()
      }

      if (e.key === 'Enter' && selected?.item) {
        const article: Article = selected.item

        return router.push(
          routes(article.locale, '', [article?.category?.slug, article?.subCategory?.slug, article.slug]).encyclopediaArticle
        )
      }
    }
  }

  /**
   * calls the selected clear on text input
   */
  const handleNoneArrowKeyPress = () => {
    if (selected.item || selected.idx !== -1) {
      clearSelected()
    }
  }

  /**
   * function for clearing the selected item
   */
  const clearSelected = () => {
    setSelected({ item: null, idx: -1 })
  }

  return (
    <div className={classNames(styles['search-bar'], className)}>
      <div className={styles['search-input-wrapper']}>
        <input
          ref={searchInput}
          className={classNames(styles['search-input'], {
            [styles['search-active']]: !!value
          })}
          type="text"
          role="searchbox"
          placeholder={placeholder}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          onKeyPress={handleNoneArrowKeyPress}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
        />
        <Search className={styles['prepend-icon']} />
        {!!value && (
          <button className={styles.clear} onClick={() => setValue('')}>
            <Cross />
          </button>
        )}
      </div>
      {!!value && isFocused && !!searchResults?.length && (
        <ul ref={searchList} className={styles['search-list']}>
          {searchResults.map((article, idx) => (
            <SearchItem key={`${article.slug}-${idx}`} item={article} query={value} selected={idx === selected?.idx} />
          ))}
        </ul>
      )}
    </div>
  )
}

export default SearchBar
